import type { ApiDateTime } from '../api/types'
import { formatShanghaiDateTime } from '../utils/dateTime'

export type LogModule = 'ALL' | 'PRODUCT' | 'ORDER' | 'CUSTOMER' | 'INVENTORY' | 'EMPLOYEE' | 'CONTRACT' | 'SETTINGS'
export type LogDatePreset = '' | 'TODAY' | 'YESTERDAY' | 'THIS_MONTH' | 'LAST_MONTH'

export interface LogFilters {
  module: LogModule
  operationCategory: string
  employeeId: string
  datePreset: LogDatePreset
  startDate: string
  endDate: string
}

export interface LogCenterItem {
  id: string
  module: Exclude<LogModule, 'ALL'>
  operationType: string
  operationCategory?: string
  operationLabel: string
  targetType?: string
  targetId?: number | string
  targetAvailable: boolean
  targetLabel?: string
  targetCode?: string
  actorUserId?: number | string
  actorName: string
  storeId?: number | string
  storeName?: string
  occurredAt: ApiDateTime
  summary?: string
  changes?: Array<{ field: string; beforeValue?: string; afterValue?: string }>
}

export interface LogEmployee {
  id: number | string
  name: string
  storeName?: string
  roleName?: string
  storeIds?: Array<number | string>
  storeNames?: string[]
}

export interface LogPage {
  list: LogCenterItem[]
  total: number
}

export const LOG_MODULES: Array<{ id: LogModule; label: string }> = [
  { id: 'ALL', label: '全部模块' }, { id: 'PRODUCT', label: '商品' },
  { id: 'ORDER', label: '订单' }, { id: 'CUSTOMER', label: '客户' },
  { id: 'INVENTORY', label: '库存' }, { id: 'EMPLOYEE', label: '员工' },
  { id: 'CONTRACT', label: '合同' }, { id: 'SETTINGS', label: '设置' },
]

export const LOG_OPERATIONS: Partial<Record<LogModule, Array<{ id: string; label: string }>>> = {
  PRODUCT: [
    { id: 'PRODUCT_PRICE', label: '修改价格' }, { id: 'PRODUCT_LOCATION', label: '移动位置' },
    { id: 'PRODUCT_CONTRACT_START', label: '发起电子合同' }, { id: 'PRODUCT_UPDATE', label: '修改商品资料' },
  ],
  ORDER: [
    { id: 'ORDER_PRICE', label: '修改价格' }, { id: 'ORDER_DATE', label: '修改下单时间' },
    { id: 'ORDER_UPDATE', label: '修改订单' },
  ],
}

export const EMPTY_LOG_FILTERS: LogFilters = {
  module: 'ALL', operationCategory: '', employeeId: '', datePreset: '', startDate: '', endDate: '',
}

const pad = (value: number) => String(value).padStart(2, '0')
const dateKey = (date: Date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`

function shanghaiToday() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date()).reduce<Record<string, string>>((result, part) => {
    result[part.type] = part.value
    return result
  }, {})
  return new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 12))
}

export function dateRangeForPreset(preset: LogDatePreset, today = shanghaiToday()): [string, string] {
  if (!preset) return ['', '']
  const year = today.getUTCFullYear(); const month = today.getUTCMonth(); const day = today.getUTCDate()
  if (preset === 'TODAY') return [dateKey(today), dateKey(today)]
  if (preset === 'YESTERDAY') {
    const yesterday = new Date(Date.UTC(year, month, day - 1, 12))
    return [dateKey(yesterday), dateKey(yesterday)]
  }
  if (preset === 'THIS_MONTH') return [dateKey(new Date(Date.UTC(year, month, 1, 12))), dateKey(today)]
  const first = new Date(Date.UTC(year, month - 1, 1, 12))
  const last = new Date(Date.UTC(year, month, 0, 12))
  return [dateKey(first), dateKey(last)]
}

export function parseLogFilters(params: URLSearchParams): LogFilters {
  const moduleValue = params.get('logModule')?.toUpperCase() as LogModule | undefined
  const module = LOG_MODULES.some((item) => item.id === moduleValue) ? moduleValue! : 'ALL'
  const preset = params.get('logDatePreset')?.toUpperCase() as LogDatePreset | undefined
  return {
    module,
    operationCategory: params.get('logOperation') || '',
    employeeId: params.get('logEmployee') || '',
    datePreset: ['', 'TODAY', 'YESTERDAY', 'THIS_MONTH', 'LAST_MONTH'].includes(preset || '') ? preset || '' : '',
    startDate: params.get('logStart') || '', endDate: params.get('logEnd') || '',
  }
}

export function writeLogFilters(params: URLSearchParams, filters: LogFilters) {
  const next = new URLSearchParams(params)
  const values: Array<[string, string]> = [
    ['logModule', filters.module === 'ALL' ? '' : filters.module], ['logOperation', filters.operationCategory],
    ['logEmployee', filters.employeeId], ['logDatePreset', filters.datePreset],
    ['logStart', filters.startDate], ['logEnd', filters.endDate],
  ]
  values.forEach(([key, value]) => { if (value) next.set(key, value); else next.delete(key) })
  return next
}

export function serializeLogFilters(filters: LogFilters, pageNo = 1, pageSize = 20) {
  const params = new URLSearchParams({ pageNo: String(pageNo), pageSize: String(pageSize) })
  if (filters.module !== 'ALL') params.set('module', filters.module)
  if (filters.operationCategory) params.set('operationCategory', filters.operationCategory)
  if (filters.employeeId) params.set('employeeId', filters.employeeId)
  if (filters.startDate) params.set('startDate', filters.startDate)
  if (filters.endDate) params.set('endDate', filters.endDate)
  return params
}

export function logFilterCount(filters: LogFilters) {
  return Number(filters.module !== 'ALL' || Boolean(filters.operationCategory))
    + Number(Boolean(filters.employeeId)) + Number(Boolean(filters.startDate || filters.endDate))
}

export function logFilterSummary(filters: LogFilters, employees: LogEmployee[] = []) {
  const module = LOG_MODULES.find((item) => item.id === filters.module)?.label || '全部模块'
  const operation = LOG_OPERATIONS[filters.module]?.find((item) => item.id === filters.operationCategory)?.label
  const employee = employees.find((item) => String(item.id) === filters.employeeId)?.name || '全部员工'
  const date = filters.datePreset
    ? ({ TODAY: '今天', YESTERDAY: '昨天', THIS_MONTH: '本月', LAST_MONTH: '上月' } as Record<string, string>)[filters.datePreset]
    : filters.startDate || filters.endDate ? '自定义时间' : '全部时间'
  return [module, operation, employee, date].filter(Boolean).join(' · ')
}

export function groupLogsByDate(items: LogCenterItem[], today = shanghaiToday()) {
  const todayKey = dateKey(today)
  const yesterdayKey = dateKey(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 1, 12)))
  const groups = new Map<string, LogCenterItem[]>()
  items.forEach((item) => {
    const key = formatShanghaiDateTime(item.occurredAt).slice(0, 10)
    const list = groups.get(key) || []
    list.push(item); groups.set(key, list)
  })
  return [...groups.entries()].map(([key, list]) => ({ key, label: key === todayKey ? '今天' : key === yesterdayKey ? '昨天' : key, list }))
}

export function logTargetPath(item: LogCenterItem) {
  if (!item.targetAvailable || !item.targetId) return null
  const search = item.targetCode || item.targetLabel || ''
  if (item.targetType === 'PRODUCT') return `/inventory?targetId=${encodeURIComponent(String(item.targetId))}&search=${encodeURIComponent(search)}`
  if (item.targetType === 'ORDER') return `/orders?search=${encodeURIComponent(search)}`
  if (item.targetType === 'CUSTOMER') return `/customers?detail=${encodeURIComponent(String(item.targetId))}&search=${encodeURIComponent(search)}`
  if (item.targetType === 'SETTINGS') return '/settings'
  return null
}
